import { JSONSchema7 } from 'json-schema';
import { types as commonTypes, customSchemas } from '@sitebuilder/common';
import { headingsSchema } from '../Header';
import StockChart from './StockChart';

const schema: JSONSchema7 = {
    title: 'Stock Chart',
    type: 'object',
    required: ['dataKey', 'xyKeys'],
    properties: {
        styles: {
            type: 'string',
            description: 'A stylesheet',
            default: '',
        },
        ...customSchemas.textSchema.TextSchema.schema,
        ...customSchemas.styleSchema.StyleSchema.schema,
        ...customSchemas.layoutSchema.LayoutSchema.schema,
        dataKey: {
            type: 'string',
            title: 'Data Key',
            default: '',
        },
        xyKeys: {
            type: 'string',
            title: 'Data Key for x and y values',
            description: 'Enter comma separated value(first be x and second be y key)',
            default: 'effectiveDate,navps',
        },
        showDistributions: {
            type: 'boolean',
            enum: [true, false],
            default: true,
            title: 'Show Distribution points',
        },
        showGrowthAmount: {
            type: 'boolean',
            enum: [true, false],
            default: false,
            title: 'Change growth amount',
        },
        chartTitles: headingsSchema,
        chartType: {
            type: 'string',
            title: 'Chart Type',
            default: 'area',
        },
        legendDropDownTitle: {
            type: 'string',
            title: 'DropDown Title',
            default: 'Compare benchmark:',
        },
        rangeButtons: {
            type: 'string',
            title: 'Chart Range Buttons',
            description: 'Enter data key(json query)',
            default: '',
        },
        toolTipValueFormat: {
            type: 'string',
            title: 'Format for tooltip value',
            default: 'curMax2',
        },
        dollarChangeValueFormat: {
            type: 'string',
            title: 'Format for dollar change value',
            default: 'curDiffMax2',
        },
        options: {
            type: 'array',
            title: 'Chart Options',
            items: {
                type: 'object',
                properties: {
                    path: { type: 'string', description: 'Options Object Path' },
                    value: { type: 'string', description: 'High Chart Options as JSON string' },
                },
            },
        },
        isShowBenchMarks: {
            type: 'boolean',
            title: 'Show Benchmarks',
            default: false,
        },
        seriesColors: {
            type: 'string',
            title: 'Series Colors',
            default: '',
        },
        isGrowthChart: {
            type: 'boolean',
            title: 'Is growth chart',
            default: false,
        },
        addOffsetMinToYtd: {
            type: 'boolean',
            title: 'Add Offset Min to YTD',
        },
    },
    dependencies: {
        showDistributions: {
            oneOf: [
                {
                    properties: {
                        showDistributions: { enum: [true] },
                        distributionKey: {
                            type: 'string',
                            title: 'Data Key Distributions',
                            default: 'distributionAmount',
                        },
                    },
                    required: ['distributionKey'],
                },
                {
                    properties: {
                        showDistributions: { enum: [false] },
                    },
                },
            ],
        },
        showGrowthAmount: {
            oneOf: [
                {
                    properties: {
                        showGrowthAmount: { enum: [true] },
                        growthAmountLabel: {
                            type: 'string',
                            title: 'Growth Amount Label',
                            default: 'Investment amount ($):',
                        },
                        initialGrowthAmount: {
                            type: 'number',
                            title: 'Initial Growth Amount',
                            default: 10000,
                        },
                    },
                    required: ['growthAmountLabel'],
                },
                {
                    properties: {
                        showGrowthAmount: { enum: [false] },
                    },
                },
            ],
        },
        ...customSchemas.layoutSchema.LayoutSchema.dependencies,
    },
};

const groups = [
    {
        'Style Editor': ['styles'],
        ...customSchemas.textSchema.TextSchema.group,
        ...customSchemas.styleSchema.StyleSchema.group,
        ...customSchemas.layoutSchema.LayoutSchema.group,
        Values: [
            'dataKey',
            'xyKeys',
            'showDistributions',
            'distributionKey',
            'showGrowthAmount',
            'growthAmountLabel',
            'initialGrowthAmount',
            'chartTitles',
            'chartType',
            'legendDropDownTitle',
            'rangeButtons',
            'toolTipValueFormat',
            'dollarChangeValueFormat',
            'options',
            'isShowBenchMarks',
            'seriesColors',
            'isGrowthChart',
            'addOffsetMinToYtd',
        ],
    },
];

const StockChartWidget: commonTypes.IWidget = {
    schema,
    uiSchema: {
        'ui:groups': groups,
        ...customSchemas.textSchema.TextSchema.uiSchema,
        ...customSchemas.styleSchema.StyleSchema.uiSchema,
        ...customSchemas.layoutSchema.LayoutSchema.uiSchema,
        styles: {
            'ui:widget': 'CSSEditor',
        },
        chartTitles: {
            items: {
                headerText: {
                    'ui:widget': 'ResourcePicker',
                },
            },
        },
    },
    templateView: StockChart,
    id: 'StockChart',
    group: 'charts',
    contentPlaceHolders: ['children'],
    initialData: () => ({
        chartType: 'area',
        initialGrowthAmount: 10000,
        chartTitles: [
            {
                headingLevel: 3,
                headerText: 'Stock Chart',
            },
        ],
    }),
};

export default StockChartWidget;
